import React, { useState, useCallback } from "react";
import ReactTable from "react-table";
import Pagination from "../../../components/DatatablePagination";
import { Input, Button } from "reactstrap";
import Creatable from "../../../components/common/CreatableSelect";
import { AddCommaNumber } from "../../../helpers/Utils";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";
import HorizontalScrollAffordance from "../../../components/common/HorizontalScrollAffordance";

const parseImportNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === "") return defaultValue;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : defaultValue;
  }

  const raw = String(value).trim();
  if (!raw) return defaultValue;

  const cleaned = raw.replace(/\s/g, "");
  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");

  if (hasComma && hasDot) {
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const decimalSeparator = lastComma > lastDot ? "," : ".";
    const thousandSeparator = decimalSeparator === "," ? "." : ",";
    const normalized = cleaned
      .replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
      .replace(decimalSeparator, ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : defaultValue;
  }

  if (hasComma) {
    const normalized = cleaned.replace(/\./g, "").replace(",", ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : defaultValue;
  }

  const normalized = cleaned.replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : defaultValue;
};

const formatImportNumber = (value, options = {}) => {
  const number = parseImportNumber(value, null);
  if (number === null) return "";
  return new Intl.NumberFormat("vi-VN", {
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(number);
};

// Custom cell renderer cho ô nhập liệu - sử dụng internal state để tránh re-render
const EditableCell = React.memo(
  ({
    value,
    onChange,
    type = "text",
    placeholder = "",
    disabled = false,
    ...rest
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    // Sync khi value từ parent thay đổi
    React.useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      setLocalValue(e.target.value);
    };

    const handleBlur = () => {
      if (localValue !== value) {
        onChange(localValue);
      }
    };

    return (
      <Input
        type={type}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        bsSize="sm"
        style={{
          textAlign: type === "number" ? "right" : "left",
          backgroundColor: disabled ? "#f0f0f0" : "",
        }}
        {...rest}
      />
    );
  },
);

// Custom cell cho ô nhập số có format phân cách nghìn (1.000.000)
const NumberEditableCell = React.memo(
  ({ value, onChange, placeholder = "0", disabled = false }) => {
    // Giữ dạng hiển thị với dấu chấm
    const [displayValue, setDisplayValue] = useState(
      value ? formatImportNumber(value) : "",
    );

    // Sync khi value từ parent thay đổi
    React.useEffect(() => {
      setDisplayValue(value ? formatImportNumber(value) : "");
    }, [value]);

    const handleChange = (e) => {
      const inputValue = e.target.value;
      // Cho phép số thập phân theo cả định dạng Việt Nam và quốc tế.
      if (inputValue === "" || /^[\d.,\s]+$/.test(inputValue)) {
        setDisplayValue(inputValue);
      }
    };

    const handleBlur = () => {
      const parsed = parseImportNumber(displayValue, null);
      const normalizedValue = parsed === null ? "" : String(parsed);
      if (normalizedValue !== String(value || "")) {
        onChange(normalizedValue);
      }
      setDisplayValue(parsed === null ? "" : formatImportNumber(parsed));
    };

    return (
      <Input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        bsSize="sm"
        style={{
          textAlign: "right",
          backgroundColor: disabled ? "#f0f0f0" : "",
        }}
      />
    );
  },
);

// ReadOnly cell component
const ReadOnlyCell = React.memo(
  ({ value, placeholder = "-", align = "center" }) => {
    return (
      <div
        tabIndex={-1}
        className={`text-${align} text-muted`}
        style={{
          padding: "6px",
          borderRadius: 4,
          fontStyle: value ? "normal" : "italic",
        }}
      >
        {value || placeholder}
      </div>
    );
  },
);

// Creatable Select cell cho nhà cung cấp
const SupplierSelectCell = injectIntl(
  React.memo(({ value, onChange, options = [], placeholder, intl }) => {
    const [localValue, setLocalValue] = useState(
      value ? { value: value, label: value } : null,
    );

    React.useEffect(() => {
      setLocalValue(value ? { value: value, label: value } : null);
    }, [value]);

    const handleChange = (selected) => {
      setLocalValue(selected);
      onChange(
        selected ? (selected.__isNew__ ? selected.label : selected.value) : "",
      );
    };

    const selectOptions = options.map((opt) => ({
      value: opt,
      label: opt,
    }));

    return (
      <Creatable
        value={localValue}
        onChange={handleChange}
        options={selectOptions}
        placeholder={placeholder}
        isClearable
        formatCreateLabel={(inputValue) =>
          intl.formatMessage(
            { id: "label.create-new-value" },
            { value: inputValue },
          )
        }
        className="react-select-sm w-100"
        classNamePrefix="react-select"
        menuPlacement="auto"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          container: (base) => ({
            ...base,
            width: "100%",
          }),
          control: (base) => ({
            ...base,
            minHeight: 32,
            height: 32,
            width: "100%",
            cursor: "pointer",
          }),
          valueContainer: (base) => ({
            ...base,
            padding: "0 6px",
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
            width: "100%",
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: 200,
          }),
          singleValue: (base) => ({
            ...base,
            fontSize: "0.8rem",
            lineHeight: "1.5",
          }),
          option: (base) => ({
            ...base,
            fontSize: "0.8rem",
            padding: "6px 12px",
          }),
          placeholder: (base) => ({
            ...base,
            fontSize: "0.8rem",
          }),
          input: (base) => ({
            ...base,
            fontSize: "0.8rem",
            margin: 0,
            padding: 0,
          }),
          indicatorSeparator: () => ({
            display: "none",
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: 4,
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
            width: "100%",
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: 200,
          }),
        }}
      />
    );
  }),
);

const ModalAddStocks = ({
  data_all = [],
  handleImportStock,
  data_providers = [],
  initialData = [],
  importReport = null,
  isImporting = false,
  intl,
}) => {
  const { messages } = intl;
  // Lấy danh sách nhà cung cấp từ props - xử lý nhiều cấu trúc có thể có
  // Lấy danh sách nhà cung cấp từ props - xử lý nhiều cấu trúc có thể có
  const supplierOptions = (data_providers || [])
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") return item;
      return item.name || item.label || null;
    })
    .filter(
      (item) =>
        item !== null && item !== undefined && String(item).trim() !== "",
    );

  // Tạo dữ liệu ban đầu với 15 dòng trống
  const createEmptyRow = useCallback(
    (index) => ({
      id: `row_${index}`,
      material_id: null,
      name: "",
      sku: "",
      barcode: "",
      unit: "",
      price: "",
      quantity: "",
      supplier: "",
      category_name: "",
      warehouse_name: "",
      min_stock_threshold: "",
      batch_code: "",
      storage_location: "",
      stock_note: "",
      paid: "",
      expiry_date: "",
      price_new: "",
      count: "",
      total: "",
      createdAt: "",
    }),
    [],
  );

  const [tableData, setTableData] = useState(() => {
    if (initialData && initialData.length > 0) {
      return initialData.map((item, index) => ({
        ...createEmptyRow(index),
        ...item,
        id: `row_init_${index}`,
      }));
    }
    return Array.from({ length: 15 }, (_, i) => createEmptyRow(i));
  });

  // Effect để cập nhật tableData khi initialData thay đổi (ví dụ khi import import từ file khác)
  React.useEffect(() => {
    if (initialData && initialData.length > 0) {
      setTableData(
        initialData.map((item, index) => ({
          ...createEmptyRow(index),
          ...item,
          id: `row_import_${index}_${Date.now()}`,
        })),
      );
    }
  }, [initialData, createEmptyRow]);

  React.useEffect(() => {
    const failedRows = importReport?.failed || [];
    if (failedRows.length > 0) {
      setTableData(
        failedRows.map((item, index) => ({
          ...createEmptyRow(index),
          ...item.data,
          price: item.data?.price_new || item.data?.price || "",
          quantity: item.data?.count || item.data?.quantity || "",
          id: `row_failed_${index}_${Date.now()}`,
          import_error: item.responseText,
        })),
      );
    }
  }, [importReport, createEmptyRow]);

  // Xử lý thay đổi giá trị ô nhập liệu - dùng useCallback để tránh re-create function
  const handleCellChange = useCallback((rowIndex, field, value) => {
    setTableData((prevData) => {
      const newData = [...prevData];
      newData[rowIndex] = {
        ...newData[rowIndex],
        [field]: value,
      };
      return newData;
    });
  }, []);

  // Thêm dòng mới
  const addNewRow = useCallback(() => {
    setTableData((prevData) => [
      ...prevData,
      {
        id: `row_${prevData.length}_${Date.now()}`,
        material_id: null,
        name: "",
        sku: "",
        barcode: "",
        unit: "",
        price: "",
        quantity: "",
        supplier: "",
        category_name: "",
        warehouse_name: "",
        min_stock_threshold: "",
        batch_code: "",
        storage_location: "",
        stock_note: "",
        paid: "",
        expiry_date: "",
        price_new: "",
        count: "",
        total: "",
        createdAt: "",
      },
    ]);
  }, []);

  // Xóa dòng
  const removeRow = useCallback((rowIndex) => {
    setTableData((prevData) => {
      if (prevData.length <= 1) return prevData;
      return prevData.filter((_, index) => index !== rowIndex);
    });
  }, []);

  // Tính toán còn lại = (giá * số lượng) - đã trả
  const calculateRemaining = (price, quantity, paid) => {
    const total =
      parseImportNumber(price, 0) * parseImportNumber(quantity, 0);
    const paidAmount = parseImportNumber(paid, 0);
    return total - paidAmount;
  };

  const rowsWithData = React.useMemo(
    () =>
      tableData.filter((row) => row.name && String(row.name).trim() !== ""),
    [tableData],
  );

  const getValidationErrors = useCallback(
    (rows = rowsWithData) => {
      const errors = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowIndex = tableData.findIndex((r) => r.id === row.id) + 1; // Số thứ tự thực tế

        // Check đơn vị
        if (!row.unit || row.unit.trim() === "") {
          errors.push(
            intl.formatMessage(
              { id: "modal.alert.missing-unit" },
              { index: rowIndex, name: row.name },
            ),
          );
        }

        // Check đơn giá
        const price = parseImportNumber(row.price, 0);
        if (price <= 0) {
          errors.push(
            intl.formatMessage(
              { id: "modal.alert.price-must-positive" },
              { index: rowIndex, name: row.name },
            ),
          );
        }

        // Check số lượng nhập
        const quantity = parseImportNumber(row.quantity, 0);
        if (quantity <= 0) {
          errors.push(
            intl.formatMessage(
              { id: "modal.alert.quantity-must-positive" },
              { index: rowIndex, name: row.name },
            ),
          );
        }
      }
      return errors;
    },
    [intl, rowsWithData, tableData],
  );

  const validationErrors = React.useMemo(
    () => getValidationErrors(rowsWithData),
    [getValidationErrors, rowsWithData],
  );

  const importPreview = React.useMemo(() => {
    const totalQuantity = rowsWithData.reduce(
      (sum, row) => sum + parseImportNumber(row.quantity, 0),
      0,
    );
    const totalValue = rowsWithData.reduce(
      (sum, row) =>
        sum +
        parseImportNumber(row.quantity, 0) * parseImportNumber(row.price, 0),
      0,
    );

    return {
      totalRows: rowsWithData.length,
      validRows: Math.max(rowsWithData.length - validationErrors.length, 0),
      errorRows: validationErrors.length,
      totalQuantity,
      totalValue,
    };
  }, [rowsWithData, validationErrors]);

  // Xử lý submit
  const handleSubmit = () => {
    if (rowsWithData.length === 0) {
      alert(messages["modal.alert.min-one-material"]);
      return;
    }

    // Hiển thị tất cả lỗi nếu có
    if (validationErrors.length > 0) {
      alert(
        messages["modal.alert.check-required"] + validationErrors.join("\n"),
      );
      return;
    }

    // Bước 3: Map dữ liệu để gửi lên server
    const dataToSubmit = rowsWithData.map((row) => ({
      name: row.name,
      unit: row.unit,
      material_id: row.material_id,
      barcode: row.barcode,
      count: parseImportNumber(row.quantity, 0),
      price_new: parseImportNumber(row.price, 0),
      supplier: row.supplier,
      paid: parseImportNumber(row.paid, 0),
      expiry_date: row.expiry_date,
      category_name: row.category_name || "",
      warehouse_name: row.warehouse_name || "",
      min_stock_threshold: parseImportNumber(row.min_stock_threshold, 0),
      batch_code: row.batch_code || "",
      storage_location: row.storage_location || "",
      stock_note: row.stock_note || "",
    }));

    // Bước 4: Check Duplicate Barcodes
    const barcodes = dataToSubmit
      .map((d) => d.barcode)
      .filter((b) => b && b.trim() !== "");

    // 4.1 Check duplicates within current input
    const uniqueBarcodes = new Set(barcodes);
    if (uniqueBarcodes.size !== barcodes.length) {
      alert(messages["modal.alert.barcode-duplicate-list"]);
      return;
    }

    // 4.2 Check duplicates against existing database (data_all)
    if (data_all && data_all.length > 0) {
      for (const item of dataToSubmit) {
        if (item.barcode && item.barcode.trim() !== "") {
          const exists = data_all.find(
            (s) =>
              s.barcode &&
              String(s.barcode).trim() === String(item.barcode).trim(),
          );
          const isSameMaterial =
            (item.material_id && String(exists?.id) === String(item.material_id)) ||
            String(exists?.name || "").trim().toLowerCase() ===
              String(item.name || "").trim().toLowerCase();
          if (exists && !isSameMaterial) {
            alert(
              intl.formatMessage(
                { id: "modal.alert.barcode-duplicate-db" },
                { barcode: item.barcode, name: exists.name },
              ),
            );
            return;
          }
        }
      }
    }

    console.log("Dữ liệu chuẩn bị gửi đi:", dataToSubmit);
    handleImportStock(dataToSubmit);
  };

  // Cấu hình cột cho ReactTable
  const columns = [
    {
      Header: <IntlMessages id="modal.stock.stt" />,
      accessor: "stt",
      width: 45,
      sortable: false,
      filterable: false,
      Cell: (row) => (
        <div
          tabIndex={-1}
          className="text-center text-muted"
          style={{ width: "100%", fontWeight: 500 }}
        >
          {row.index + 1}
        </div>
      ),
    },
    {
      Header: () => (
        <span>
          <IntlMessages id="stock.name" />{" "}
          <span style={{ color: "red" }}>*</span>
        </span>
      ),
      accessor: "name",
      minWidth: 180,
      Cell: (row) => (
        <EditableCell
          key={`name_${row.index}`}
          value={row.original.name}
          onChange={(val) => handleCellChange(row.index, "name", val)}
          placeholder={intl.formatMessage({ id: "stock.name" })}
          list="stock-name-suggestions"
        />
      ),
    },
    {
      Header: <IntlMessages id="modal.stock.sku" />,
      accessor: "sku",
      width: 100,
      Cell: (row) => (
        <ReadOnlyCell
          value={row.value}
          placeholder={intl.formatMessage({ id: "modal.auto-generate" })}
        />
      ),
    },
    {
      Header: <IntlMessages id="modal.stock.barcode" />,
      accessor: "barcode",
      width: 150,
      Cell: (row) => (
        <EditableCell
          key={`barcode_${row.index}`}
          value={row.original.barcode}
          onChange={(val) => handleCellChange(row.index, "barcode", val)}
          placeholder={intl.formatMessage({ id: "modal.stock.barcode" })}
        />
      ),
    },
    {
      Header: () => (
        <span>
          <IntlMessages id="stock.unit" />{" "}
          <span style={{ color: "red" }}>*</span>
        </span>
      ),
      accessor: "unit",
      width: 85,
      Cell: (row) => (
        <EditableCell
          key={`unit_${row.index}`}
          value={row.original.unit}
          onChange={(val) => handleCellChange(row.index, "unit", val)}
          placeholder={intl.formatMessage({ id: "stock.unit" })}
        />
      ),
    },
    {
      Header: <IntlMessages id="stock.dashboard.category" />,
      accessor: "category_name",
      minWidth: 140,
      Cell: (row) => (
        <EditableCell
          key={`category_name_${row.index}`}
          value={row.original.category_name}
          onChange={(val) => handleCellChange(row.index, "category_name", val)}
          placeholder={intl.formatMessage({ id: "stock.dashboard.category" })}
        />
      ),
    },
    {
      Header: <IntlMessages id="stock.dashboard.warehouse" />,
      accessor: "warehouse_name",
      minWidth: 120,
      Cell: (row) => (
        <EditableCell
          key={`warehouse_name_${row.index}`}
          value={row.original.warehouse_name}
          onChange={(val) => handleCellChange(row.index, "warehouse_name", val)}
          placeholder={intl.formatMessage({ id: "stock.dashboard.warehouse" })}
        />
      ),
    },
    {
      Header: <IntlMessages id="stock.dashboard.min-stock-threshold" />,
      accessor: "min_stock_threshold",
      width: 120,
      Cell: (row) => (
        <NumberEditableCell
          key={`min_stock_threshold_${row.index}`}
          value={row.original.min_stock_threshold}
          onChange={(val) =>
            handleCellChange(row.index, "min_stock_threshold", val)
          }
          placeholder="0"
        />
      ),
    },
    {
      Header: "Mã lô",
      accessor: "batch_code",
      minWidth: 130,
      Cell: (row) => (
        <EditableCell
          key={`batch_code_${row.index}`}
          value={row.original.batch_code}
          onChange={(val) => handleCellChange(row.index, "batch_code", val)}
          placeholder="LOT-2026-001"
        />
      ),
    },
    {
      Header: "Vị trí lưu kho",
      accessor: "storage_location",
      minWidth: 150,
      Cell: (row) => (
        <EditableCell
          key={`storage_location_${row.index}`}
          value={row.original.storage_location}
          onChange={(val) =>
            handleCellChange(row.index, "storage_location", val)
          }
          placeholder="Kệ A1"
        />
      ),
    },
    {
      Header: "Ghi chú",
      accessor: "stock_note",
      minWidth: 180,
      Cell: (row) => (
        <EditableCell
          key={`stock_note_${row.index}`}
          value={row.original.stock_note}
          onChange={(val) => handleCellChange(row.index, "stock_note", val)}
          placeholder="Ghi chú nhập kho"
        />
      ),
    },
    {
      Header: <IntlMessages id="modal.stock.supplier" />,
      accessor: "supplier",
      minWidth: 160,
      Cell: (row) => (
        <SupplierSelectCell
          key={`supplier_${row.index}`}
          value={row.original.supplier}
          onChange={(val) => handleCellChange(row.index, "supplier", val)}
          options={supplierOptions}
          placeholder={intl.formatMessage({ id: "modal.stock.supplier" })}
        />
      ),
    },
    {
      Header: () => (
        <span>
          <IntlMessages id="product.price.cost" />{" "}
          <span style={{ color: "red" }}>*</span>
        </span>
      ),
      accessor: "price",
      width: 120,
      Cell: (row) => (
        <NumberEditableCell
          key={`price_${row.index}`}
          value={row.original.price}
          onChange={(val) => handleCellChange(row.index, "price", val)}
          placeholder="0"
        />
      ),
    },
    {
      Header: () => (
        <span>
          <IntlMessages id="warehouse.import-quantity" />{" "}
          <span style={{ color: "red" }}>*</span>
        </span>
      ),
      accessor: "quantity",
      width: 150,
      Cell: (row) => (
        <NumberEditableCell
          key={`quantity_${row.index}`}
          value={row.original.quantity}
          onChange={(val) => handleCellChange(row.index, "quantity", val)}
          placeholder="0"
        />
      ),
    },
    {
      Header: () => (
        <span>
          <IntlMessages id="modal.product.paid" />{" "}
          <span style={{ color: "red" }}>*</span>
        </span>
      ),
      accessor: "paid",
      width: 110,
      Cell: (row) => (
        <NumberEditableCell
          key={`paid_${row.index}`}
          value={row.original.paid}
          onChange={(val) => handleCellChange(row.index, "paid", val)}
          placeholder="0"
        />
      ),
    },
    {
      Header: <IntlMessages id="modal.stock.remaining" />,
      accessor: "remaining",
      width: 100,
      headerStyle: { textAlign: "right", justifyContent: "flex-end" },
      Cell: (row) => {
        const remaining = calculateRemaining(
          row.original.price,
          row.original.quantity,
          row.original.paid,
        );
        return (
          <div
            className={`text-right ${
              remaining > 0 ? "text-danger" : "text-success"
            }`}
            style={{
              padding: "6px",
              fontWeight: remaining > 0 ? "bold" : "normal",
            }}
          >
            {remaining !== 0 ? AddCommaNumber(remaining) : "-"}
          </div>
        );
      },
    },
    {
      Header: <IntlMessages id="modal.stock.expired" />,
      accessor: "expiry_date",
      width: 130,
      Cell: (row) => (
        <Input
          type="date"
          value={row.original.expiry_date || ""}
          onChange={(e) =>
            handleCellChange(row.index, "expiry_date", e.target.value)
          }
          bsSize="sm"
          style={{ height: 32 }}
        />
      ),
    },
    {
      Header: "",
      accessor: "actions",
      width: 50,
      sortable: false,
      filterable: false,
      Cell: (row) => (
        <Button
          tabIndex={-1}
          color="link"
          size="sm"
          className="text-danger p-0"
          onClick={() => removeRow(row.index)}
          title="Xóa dòng"
        >
          <i className="simple-icon-trash" />
        </Button>
      ),
    },
  ];

  return (
    <div className="stock-entry-table-container">
      <style>{`
        .stock-entry-table-container .rt-thead.-header {
          box-shadow: none;
          // border-bottom: 2px solid #dee2e6;
        }
        .stock-entry-table-container .rt-th {
          padding: 10px 5px;
          font-weight: 600;
          // background-color: #f8f9fa;
        }
        .stock-entry-table-container .rt-td {
          padding: 4px 5px;
          display: flex;
          align-items: center;
        }
        .stock-entry-table-container .rt-tr,
        .stock-entry-table-container .rt-td {
          outline: none !important;
        }
        .stock-entry-table-container input.form-control-sm {
          height: 32px;
        }
        .stock-entry-table-container .react-select-sm .react-select__control {
          min-height: 32px;
        }
        .stock-import-preview-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(120px, 1fr));
          gap: 8px;
        }
        .stock-import-preview-card {
          border: 1px solid #e6ebf2;
          border-radius: 10px;
          padding: 10px 12px;
          background: #fbfcfe;
        }
        .stock-import-result-box {
          border-radius: 10px;
          border: 1px solid #e6ebf2;
          background: #fff;
          max-height: 180px;
          overflow-y: auto;
        }
        /* LEGACY_STOCK_IMPORT_SCROLL_AFFORDANCE_LOCAL_STYLE_START
        Local horizontal scroll styles moved to HorizontalScrollAffordance.
        LEGACY_STOCK_IMPORT_SCROLL_AFFORDANCE_LOCAL_STYLE_END */
        @media (max-width: 991px) {
          .stock-import-preview-grid {
            grid-template-columns: repeat(2, minmax(120px, 1fr));
          }
        }
      `}</style>

      {/* Datalist for Stock Name Suggestions */}
      <datalist id="stock-name-suggestions">
        {data_all && data_all.map((s, i) => <option key={i} value={s.name} />)}
      </datalist>

      <div className="stock-import-preview-grid mb-3">
        <div className="stock-import-preview-card">
          <div className="text-muted text-small">Dòng có dữ liệu</div>
          <div className="font-weight-bold">{formatImportNumber(importPreview.totalRows)}</div>
        </div>
        <div className="stock-import-preview-card">
          <div className="text-muted text-small">Dòng cần kiểm tra</div>
          <div className={importPreview.errorRows ? "font-weight-bold text-danger" : "font-weight-bold text-success"}>
            {formatImportNumber(importPreview.errorRows)}
          </div>
        </div>
        <div className="stock-import-preview-card">
          <div className="text-muted text-small">Tổng số lượng nhập</div>
          <div className="font-weight-bold">{formatImportNumber(importPreview.totalQuantity)}</div>
        </div>
        <div className="stock-import-preview-card">
          <div className="text-muted text-small">Tổng giá trị nhập</div>
          <div className="font-weight-bold">
            {formatImportNumber(Math.round(importPreview.totalValue))} đ
          </div>
        </div>
        <div className="stock-import-preview-card">
          <div className="text-muted text-small">Trạng thái</div>
          <div className={validationErrors.length ? "font-weight-bold text-danger" : "font-weight-bold text-success"}>
            {rowsWithData.length === 0
              ? "Chưa có dữ liệu"
              : validationErrors.length
                ? "Cần bổ sung"
                : "Sẵn sàng nhập"}
          </div>
        </div>
      </div>

      {importReport && (
        <div className="mb-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
            <strong>Kết quả nhập kho</strong>
            <span className="text-muted text-small">
              Thành công: {formatImportNumber(importReport.successful?.length || 0)} | Thất bại:{" "}
              {formatImportNumber(importReport.failed?.length || 0)}
            </span>
          </div>
          <div className="stock-import-result-box p-2">
            {(importReport.successful || []).map((item, index) => (
              <div key={`success-${index}`} className="d-flex align-items-start text-success mb-1">
                <i className="simple-icon-check mr-2 mt-1" />
                <span>{item.responseText || item.data?.name || "Nhập thành công"}</span>
              </div>
            ))}
            {(importReport.failed || []).map((item, index) => (
              <div key={`failed-${index}`} className="d-flex align-items-start text-danger mb-1">
                <i className="simple-icon-close mr-2 mt-1" />
                <span>{item.responseText || item.data?.name || "Nhập thất bại"}</span>
              </div>
            ))}
            {importReport.failed?.length > 0 && (
              <div className="text-muted text-small mt-2">
                Các dòng thất bại đã được giữ lại trong bảng bên dưới để bạn chỉnh và nhập lại.
              </div>
            )}
          </div>
        </div>
      )}

      {/* LEGACY_STOCK_IMPORT_NATIVE_SCROLL_TABLE_START
      <div className="stock-entry-scroll-toolbar d-flex align-items-center mb-2">
        <i className="simple-icon-cursor-move mr-2" />
        Bảng có nhiều cột, bạn có thể kéo thanh cuộn ngang bên dưới để xem các cột phía sau.
      </div>

      <div className="stock-entry-table-scroll-shell">
        <div className="stock-entry-table-scroll-inner">
      LEGACY_STOCK_IMPORT_NATIVE_SCROLL_TABLE_END */}
      <HorizontalScrollAffordance
        minWidth={1600}
        hint="Bảng có nhiều cột, bạn có thể kéo ngang vùng bảng hoặc dùng nút trượt để xem các cột phía sau."
      >
        <ReactTable
          data={tableData}
          columns={columns}
          defaultPageSize={15}
          showPageSizeOptions={false}
          showPagination={tableData.length > 15}
          minRows={1}
          NoDataComponent={() => null}
          className="-striped -highlight"
          PaginationComponent={Pagination}
          getTdProps={() => ({
            tabIndex: -1,
            style: { outline: "none" },
          })}
          getTrProps={() => ({
            tabIndex: -1,
            style: { outline: "none" },
          })}
        />
      </HorizontalScrollAffordance>
      {/* LEGACY_STOCK_IMPORT_NATIVE_SCROLL_TABLE_CLOSE_START
        </div>
      </div>
      LEGACY_STOCK_IMPORT_NATIVE_SCROLL_TABLE_CLOSE_END */}

      <div className="d-flex justify-content-between align-items-center mt-3">
        <Button color="outline-secondary" size="sm" onClick={addNewRow}>
          <i className="simple-icon-plus mr-1" />
          <IntlMessages id="modal.add-row" />
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isImporting || rowsWithData.length === 0}
        >
          <i className="simple-icon-check mr-2" />
          {isImporting ? "Đang nhập..." : <IntlMessages id="modal.stock.confirm-import" />}
        </Button>
      </div>
    </div>
  );
};

export default injectIntl(ModalAddStocks);
